import * as Sentry from '@sentry/react';
import logger from './services/logger';
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';
import './index.css';
import { registerSW } from 'virtual:pwa-register';
import { useUIStore } from './stores/useUIStore';
import { hasTrackingConsent } from './services/analytics-ga4';

// Initialize Sentry (client-side error tracking).
//
// §25 TDDDG / DSGVO: plain error capture runs under legitimate interest (and is
// listed as such in the privacy notice). Session Replay + performance tracing
// are consent-sensitive and are therefore only enabled once the user has
// granted analytics/tracking consent — at boot if already granted, or live via
// enableSentryTrackingIntegrations() when consent is given mid-session.
const win = window as any;
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const REPLAY_OPTS = { maskAllText: true, maskAllInputs: true, blockAllMedia: true };
const trackingConsentAtBoot = hasTrackingConsent();
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    // Must be bare identifiers: Vite's `define` only substitutes those, not
    // property access like `win.__SENTRY_RELEASE__`. Reading them off window
    // yielded undefined, so every event was tagged 'fahryo@dev' while the
    // source maps were uploaded under the real release — they never matched
    // and all production stack traces stayed minified.
    release:
      typeof __SENTRY_RELEASE__ === 'string'
        ? __SENTRY_RELEASE__
        : `fahryo@${typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'dev'}`,
    integrations: trackingConsentAtBoot
      ? [Sentry.browserTracingIntegration(), Sentry.replayIntegration(REPLAY_OPTS)]
      : [],
    tracePropagationTargets: ['localhost', /^https:\/\/(app|admin)\.fahryo\.de/],
    tracesSampleRate: trackingConsentAtBoot ? 0.2 : 0, // performance: consent-gated
    replaysSessionSampleRate: 0, // Don't record normal sessions
    replaysOnErrorSampleRate: trackingConsentAtBoot ? 1.0 : 0, // consent-gated
    beforeSend(event) {
      // Don't send errors in development
      if (import.meta.env.DEV) return null;

      // ── Drop info-level Sentry.captureMessage events ──
      // The analytics layer uses captureMessage(level=info) for funnel
      // telemetry. Those should appear in Sentry's Logs view, not as issues.
      // Replays + Performance metrics still capture them.
      if (event.level === 'info') return null;

      // ── Extract error info (used in all checks below) ──
      const errorMsg = event.exception?.values?.[0]?.value || '';
      const errorType = event.exception?.values?.[0]?.type || '';

      // ── Drop transient / expected errors entirely ──────────────────────
      // These were previously fingerprinted (deduped) but still created
      // issues. Now they're dropped — they're not actionable, they pollute
      // the issue list, and Replays still capture the surrounding session
      // for debugging when needed.

      // AbortErrors — user cancelled / iOS background throttle (FAHRYO-1B/1C)
      if (
        errorType === 'AbortError' ||
        errorMsg.match(/AbortError|The operation was aborted|signal is aborted/i)
      ) {
        return null;
      }
      // Network errors — offline, DNS, transient socket
      if (
        errorMsg.match(
          /Failed to fetch|Load failed|NetworkError|ERR_INTERNET|ECONNREFUSED|net::ERR|FetchEvent\.respondWith/i
        )
      ) {
        return null;
      }
      // Auth-expiry — expected session refresh path
      if (
        errorMsg.match(/^Unauthorized$|401|unauthorized|invalid.*token|session.*expired/i) ||
        errorType === 'AuthError'
      ) {
        return null;
      }
      // Chunk loading — old SW serving a deploy that no longer exists; SW handles auto-update
      if (
        errorMsg.match(
          /Loading chunk|ChunkLoadError|dynamically imported|Importing a module script failed/i
        )
      ) {
        return null;
      }
      // Rate limits — server already returned a structured response
      if (errorMsg.match(/^429$|rate.?limit|too many/i)) {
        return null;
      }
      // Supabase generic transport errors (PGRST handled below as a real issue)
      if (
        (errorMsg.includes('Supabase') || errorMsg.includes('supabase.co')) &&
        errorMsg.match(/Failed to fetch|Load failed|AbortError/i)
      ) {
        return null;
      }

      // ── Custom Fingerprinting: Group remaining known error classes ─────

      // Supabase errors with codes → group by error code (real issue)
      if (errorMsg.includes('PGRST')) {
        const code = errorMsg.match(/PGRST\d+|[0-9]{5}/)?.[0] || 'unknown';
        event.fingerprint = ['supabase-error', code];
        event.tags = { ...event.tags, error_class: 'supabase' };
      }
      // AI/Gemini timeouts → single issue (cost-relevant, keep)
      else if (errorMsg.match(/timed out|AI request|DEADLINE_EXCEEDED|gemini/i)) {
        event.fingerprint = ['ai-timeout'];
        event.tags = { ...event.tags, error_class: 'ai_timeout' };
      }

      // ── DSGVO: Strip PII from user context ──
      if (event.user?.email) delete event.user.email;
      if (event.user?.username) delete event.user.username;
      if (event.user?.ip_address) delete event.user.ip_address;
      // Strip email-related data from breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((b) => ({
          ...b,
          data: b.data
            ? Object.fromEntries(
                Object.entries(b.data).filter(
                  ([k, v]) =>
                    !k.toLowerCase().includes('email') &&
                    !(typeof v === 'string' && v.includes('@'))
                )
              )
            : undefined,
        }));
      }
      return event;
    },
    beforeSendTransaction(event) {
      // Strip tokens from transaction URLs
      if (event.request?.url) {
        event.request.url = event.request.url.replace(/token=[^&]+/g, 'token=REDACTED');
      }
      if (event.request?.query_string && typeof event.request.query_string === 'string') {
        event.request.query_string = event.request.query_string.replace(
          /token=[^&]+/g,
          'token=REDACTED'
        );
      }
      return event;
    },
  });
}

// Enable consent-gated Sentry integrations (Session Replay + performance
// tracing) when the user grants analytics consent mid-session. Idempotent:
// Sentry ignores re-adding an already-active integration of the same name.
export function enableSentryTrackingIntegrations(): void {
  if (!sentryDsn) return;
  try {
    Sentry.addIntegration(Sentry.browserTracingIntegration());
    Sentry.addIntegration(Sentry.replayIntegration(REPLAY_OPTS));
  } catch (e) {
    logger.warn('Sentry', 'Failed to enable tracking integrations', e as any);
  }
}

// Helper: Set Sentry user context (called from App.tsx on login/logout)
export function setSentryUser(
  user: { id: string; email?: string; isPremium?: boolean; language?: string } | null
) {
  if (user) {
    // DSGVO: Only set user ID — no email or other PII
    Sentry.setUser({ id: user.id });
    Sentry.setTag('user.isPremium', String(user.isPremium ?? false));
    Sentry.setTag('user.language', user.language ?? 'de');
  } else {
    Sentry.setUser(null);
  }
}

if ('serviceWorker' in navigator) {
  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      // F-20: Don't silently reload — even outside an exam session, a
      // surprise reload is jarring (loses unsaved chat input, scroll
      // position, draft answers, etc.). Behavior matrix:
      //   • exam/learn session running → defer until session ends
      //     (existing behavior, kept)
      //   • otherwise → show a sticky banner with "Update now" /
      //     "Later" buttons; user decides when to apply.
      const sessionActive = document.querySelector('[data-session-active="true"]');
      const apply = () => {
        logger.info('SW', 'User confirmed update — applying');
        updateSW(true);
      };
      if (sessionActive) {
        logger.info('SW', 'Update available but session active — deferring');
        win.__FAHRYO_PENDING_UPDATE = () => {
          logger.info('SW', 'Session ended — showing deferred update prompt');
          // After the session, also route through the prompt — never
          // ambush the user with an immediate reload.
          useUIStore.getState().setUpdatePrompt(true, apply);
        };
      } else {
        logger.info('SW', 'New version available — prompting user');
        useUIStore.getState().setUpdatePrompt(true, apply);
      }
    },
    onOfflineReady() {
      logger.info('SW', 'App ready for offline use');
    },
    onRegisteredSW(_swUrl, registration) {
      if (registration) {
        // Check for SW updates every 10 minutes (was 60 min — too slow for mobile)
        setInterval(
          () => {
            registration.update();
          },
          10 * 60 * 1000
        );

        // Also check for updates when user returns to the app (tab focus / phone unlock)
        // This is critical for mobile PWAs where the app stays in background for hours
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            logger.info('SW', 'App became visible — checking for updates...');
            registration.update();
          }
        });

        // Check immediately on first launch (after a brief delay for network)
        setTimeout(() => registration.update(), 2000);
      }
    },
  });
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Could not find root element to mount to');
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);
